import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ShieldAlert, Zap, MapPin, Phone, AlertCircle, HeartPulse, Flame, CarFront, Lock, Info } from 'lucide-react';
import { toast } from 'sonner';

interface SOSModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (report: any) => void;
  user: any;
  neighbors?: { id: string; name: string; distance: string; avatar: string }[];
  onSyncContacts?: () => void;
  isSyncing?: boolean;
}

export const SOSModal: React.FC<SOSModalProps> = ({ isOpen, onClose, onSubmit, user, neighbors = [], onSyncContacts, isSyncing }) => {
  const [type, setType] = React.useState<'Security' | 'Medical' | 'Fire' | 'Accident'>('Security');
  const [location, setLocation] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [state, setState] = React.useState('Lagos');
  const [lga, setLga] = React.useState('');
  const [shareScope, setShareScope] = React.useState<'Community' | 'Selected'>('Community');
  const [selectedFriends, setSelectedFriends] = React.useState<string[]>([]);
  const [showSafeCodeInfo, setShowSafeCodeInfo] = React.useState(false);

  const types = [
    { id: 'Security', label: 'Security/Threat', icon: ShieldAlert, color: 'bg-red-500' },
    { id: 'Medical', label: 'Medical Emergency', icon: HeartPulse, color: 'bg-blue-500' },
    { id: 'Fire', label: 'Fire Outbreak', icon: Flame, color: 'bg-orange-500' },
    { id: 'Accident', label: 'Serious Accident', icon: CarFront, color: 'bg-zinc-800' },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!location || !description) {
      toast.error('Please provide location and details');
      return;
    }
    if (shareScope === 'Selected' && selectedFriends.length === 0) {
      toast.error('Please select at least one neighbor for private alert');
      return;
    }
    onSubmit({
      user: user?.name || 'Anonymous',
      type,
      location,
      state,
      lga,
      description,
      scope: shareScope,
      recipients: shareScope === 'Selected' ? selectedFriends : 'Community'
    });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          >
            <div className="bg-red-600 p-6 text-white relative shrink-0">
              <button 
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors z-50"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 rounded-xl">
                  <ShieldAlert className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-black uppercase tracking-tight italic">SOS Broadcast</h2>
                  <p className="text-[10px] font-bold text-white/80 uppercase">Sentlo Emergency Response</p>
                </div>
              </div>
              <p className="text-sm font-medium text-red-100">
                This will alert all Sentlo users and official agencies within 5km of your location.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                {types.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setType(t.id as any)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all ${
                      type === t.id 
                        ? 'border-red-600 bg-red-50 text-red-600' 
                        : 'border-zinc-100 bg-zinc-50 text-zinc-400 hover:border-zinc-200'
                    }`}
                  >
                    <t.icon className={`w-6 h-6 ${type === t.id ? 'text-red-600' : 'text-zinc-400'}`} />
                    <span className="text-[10px] font-black uppercase text-center leading-tight">{t.label}</span>
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-zinc-400 ml-1">State</label>
                    <select
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2 text-sm font-bold focus:outline-hidden focus:ring-2 focus:ring-red-600/10 focus:border-red-600 transition-all"
                    >
                      <option>Lagos</option>
                      <option>Abuja (FCT)</option>
                      <option>Ogun</option>
                      <option>Oyo</option>
                      <option>Rivers</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-zinc-400 ml-1">LGA / Area</label>
                    <input
                      type="text"
                      placeholder="e.g. Ikeja"
                      value={lga}
                      onChange={(e) => setLga(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2 text-sm font-bold placeholder:text-zinc-300 focus:outline-hidden focus:ring-2 focus:ring-red-600/10 focus:border-red-600 transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-zinc-400 ml-1">Specific Landmark / Location</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-300" />
                    <input
                      type="text"
                      placeholder="Near ABC Bus Stop, Ozumba Mbadiwe..."
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-bold placeholder:text-zinc-300 focus:outline-hidden focus:ring-2 focus:ring-red-600/10 focus:border-red-600 transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-zinc-400 ml-1">Emergency Details</label>
                  <textarea
                    placeholder="Describe the situation... (e.g. 2 armed men spotted, medical help needed for fainting spell)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm font-bold placeholder:text-zinc-300 focus:outline-hidden focus:ring-2 focus:ring-red-600/10 focus:border-red-600 transition-all resize-none"
                  />
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase text-zinc-400 ml-1">Who should see this?</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShareScope('Community')}
                      className={`flex-1 py-2 px-3 rounded-xl border-2 text-[10px] font-black uppercase transition-all ${
                        shareScope === 'Community' ? 'border-red-600 bg-red-50 text-red-600' : 'border-zinc-100 bg-zinc-50 text-zinc-400'
                      }`}
                    >
                      Everyone Nearby
                    </button>
                    <button
                      type="button"
                      onClick={() => setShareScope('Selected')}
                      className={`flex-1 py-2 px-3 rounded-xl border-2 text-[10px] font-black uppercase transition-all ${
                        shareScope === 'Selected' ? 'border-red-600 bg-red-50 text-red-600' : 'border-zinc-100 bg-zinc-50 text-zinc-400'
                      }`}
                    >
                      Private Alert
                    </button>
                  </div>

                  {shareScope === 'Selected' && (
                    <div className="bg-zinc-50 rounded-2xl p-3 border border-zinc-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[9px] font-black text-zinc-400 uppercase ml-1">Select Neighbors/Friends</p>
                        <button
                          type="button"
                          onClick={onSyncContacts}
                          disabled={isSyncing}
                          className="text-[9px] font-black text-green-600 uppercase flex items-center gap-1 hover:bg-green-50 px-2 py-1 rounded-lg transition-colors"
                        >
                          {isSyncing ? 'Syncing...' : 'Sync Contacts'}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {neighbors.map(neighbor => (
                          <button
                            key={neighbor.id}
                            type="button"
                            onClick={() => {
                              setSelectedFriends(prev => 
                                prev.includes(neighbor.id) 
                                  ? prev.filter(id => id !== neighbor.id)
                                  : [...prev, neighbor.id]
                              )
                            }}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-all ${
                              selectedFriends.includes(neighbor.id)
                                ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-600/20'
                                : 'bg-white border-zinc-200 text-zinc-600'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold ${
                              selectedFriends.includes(neighbor.id) ? 'bg-white/20' : 'bg-zinc-100 text-zinc-400'
                            }`}>
                              {neighbor.avatar}
                            </div>
                            <span className="text-[10px] font-bold">{neighbor.name}</span>
                          </button>
                        ))}
                      </div>
                      {selectedFriends.length === 0 && (
                        <p className="text-[8px] text-red-400 font-bold mt-2 ml-1 italic">Please select at least one friend.</p>
                      )}
                    </div>
                  )}

                  <div className="bg-zinc-900 rounded-2xl p-4 text-white">
                    <button
                      type="button"
                      onClick={() => setShowSafeCodeInfo(!showSafeCodeInfo)}
                      className="w-full flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-amber-400" />
                        <span className="text-[10px] font-black uppercase tracking-wider">Safe Code Active</span>
                      </div>
                      <Info className="w-3.5 h-3.5 text-zinc-500 group-hover:text-white transition-colors" />
                    </button>
                    {showSafeCodeInfo && (
                      <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
                        <p className="text-[10px] text-zinc-400 leading-relaxed">
                          Your default Safe Code is <span className="text-white font-bold">1234</span>. Use this to resolve an SOS.
                        </p>
                        <p className="text-[10px] text-zinc-400 leading-relaxed">
                          Entering <span className="text-red-400 font-bold">0000</span> (Duress PIN) will hide the SOS for you but silently alert security that you are being coerced.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full bg-red-600 text-white py-4 rounded-2xl text-sm font-black uppercase tracking-widest shadow-xl shadow-red-600/20 hover:bg-red-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  <Zap className="w-5 h-5 fill-current" />
                  Broadcast SOS Now
                </button>
                <p className="text-[9px] text-center text-zinc-400 font-bold uppercase mt-3 tracking-tighter">
                  Abuse of SOS leads to permanent ban and police report.
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
